import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ProductPanelFilter }
    from '../../../types/productpanel/ProductPanelFilter';

import { ProductPanelItem }
    from '../../../types/productpanel/ProductPanelItem';

import { ProductCategory }
    from '../../../types/productpanel/ProductCategory';

import { ProductSubCategory }
    from '../../../types/productpanel/ProductSubCategory';

import { ProductBrand }
    from '../../../types/productpanel/ProductBrand';

interface ProductPanelState {

    filteredProducts: ProductPanelItem[];

    categories: ProductCategory[];

    subCategories: ProductSubCategory[];

    brands: ProductBrand[];

    productsByCategory: ProductPanelItem[];

    paginator: any;

    currentPage: number;

    pageSize: number;

    isLoading: boolean;

    currentFilter: ProductPanelFilter | null;

    selectedCategoryId: number | null;

    selectedSubCategoryId: number | null;

    selectedBrandId: number | null;
}

const initialState: ProductPanelState = {

    filteredProducts: [],

    categories: [],

    subCategories: [],

    brands: [],

    productsByCategory: [],

    paginator: null,

    currentPage: 0,

    pageSize: 12,

    isLoading: false,

    currentFilter: null,

    selectedCategoryId: null,

    selectedSubCategoryId: null,

    selectedBrandId: null,
};

const productPanelSlice = createSlice({

    name: 'productPanel',

    initialState,

    reducers: {

        // =====================================================
        // 🔹 Products
        // =====================================================

        setFilteredProducts(
            state,
            action: PayloadAction<{
                content: ProductPanelItem[];
                paginator: any;
            }>
        ) {

            state.filteredProducts =
                action.payload.content;

            state.paginator =
                action.payload.paginator;
        },

        // =====================================================
        // 🔹 Categories
        // =====================================================

        setCategories(
            state,
            action: PayloadAction<ProductCategory[]>
        ) {

            state.categories =
                action.payload;
        },

        // =====================================================
        // 🔹 Sub Categories
        // =====================================================

        setSubCategories(
            state,
            action: PayloadAction<ProductSubCategory[]>
        ) {

            state.subCategories =
                action.payload;
        },

        // =====================================================
        // 🔹 Brands
        // =====================================================

        setBrands(
            state,
            action: PayloadAction<ProductBrand[]>
        ) {

            state.brands =
                action.payload;
        },

        // =====================================================
        // 🔹 Pagination
        // =====================================================

        setPageSize(
            state,
            action: PayloadAction<number>
        ) {

            state.pageSize =
                action.payload;
        },

        setCurrentPage(
            state,
            action: PayloadAction<number>
        ) {

            state.currentPage =
                action.payload;
        },

        // =====================================================
        // 🔹 Selected Filters
        // =====================================================

        setSelectedCategoryId(
            state,
            action: PayloadAction<number | null>
        ) {

            state.selectedCategoryId =
                action.payload;
        },

        setSelectedSubCategoryId(
            state,
            action: PayloadAction<number | null>
        ) {

            state.selectedSubCategoryId =
                action.payload;
        },

        setSelectedBrandId(
            state,
            action: PayloadAction<number | null>
        ) {

            state.selectedBrandId =
                action.payload;
        },

        // =====================================================
        // 🔹 State
        // =====================================================

        setIsLoading(
            state,
            action: PayloadAction<boolean>
        ) {

            state.isLoading =
                action.payload;
        },

        setCurrentFilter(
            state,
            action: PayloadAction<ProductPanelFilter>
        ) {

            state.currentFilter =
                action.payload;
        },

        // =====================================================
        // 🔹 Reset
        // =====================================================

        clearProductPanelState(
            state
        ) {

            state.filteredProducts = [];

            state.categories = [];

            state.subCategories = [];

            state.brands = [];

            state.productsByCategory = [];

            state.paginator = null;

            state.currentFilter = null;

            state.selectedCategoryId = null;

            state.selectedSubCategoryId = null;

            state.selectedBrandId = null;
        },
    },
});

export const {

    setFilteredProducts,

    setCategories,

    setSubCategories,

    setBrands,

    setPageSize,

    setCurrentPage,

    setSelectedCategoryId,

    setSelectedSubCategoryId,

    setSelectedBrandId,

    setIsLoading,

    setCurrentFilter,

    clearProductPanelState,

} = productPanelSlice.actions;

export default productPanelSlice.reducer;